import React, { useState, useEffect, useRef } from "react";
import { MessageSquare, X, Send, Sparkles } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export default function StylistChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const chatEndRef = useRef(null);

  // Initialize or fetch session ID and history
  useEffect(() => {
    let storedSession = localStorage.getItem("wovll_stylist_session");
    if (!storedSession) {
      storedSession = `wovll_session_${Math.random().toString(36).substring(2, 11)}`;
      localStorage.setItem("wovll_stylist_session", storedSession);
    }
    setSessionId(storedSession);

    // Fetch existing messages from DB
    const fetchChatHistory = async () => {
      try {
        const response = await axios.get(`${API}/stylist/chat/${storedSession}`);
        if (response.data && response.data.messages) {
          setMessages(response.data.messages);
        }
      } catch (err) {
        console.error("Error fetching chat history", err);
      }
    };
    fetchChatHistory();
  }, []);

  // Auto-scroll chat to bottom
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isOpen]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim() || isStreaming) return;

    const userText = message;
    setMessage("");

    // Add user message to state
    const newUserMsg = { role: "user", content: userText };
    setMessages((prev) => [...prev, newUserMsg]);
    setIsStreaming(true);

    // Add empty assistant placeholder for streaming
    setMessages((prev) => [...prev, { role: "assistant", content: "" }]);

    try {
      const response = await fetch(`${API}/stylist/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          sessionId,
          message: userText,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to connect to stylist");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let done = false;
      let streamedResponse = "";

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;
        const chunk = decoder.decode(value, { stream: !done });
        
        // Split by lines to extract SSE data
        const lines = chunk.split("\n\n");
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const dataToken = line.slice(6);
            streamedResponse += dataToken;
            
            // Update the last message (the assistant one)
            setMessages((prev) => {
              const updated = [...prev];
              if (updated.length > 0) {
                updated[updated.length - 1] = {
                  role: "assistant",
                  content: streamedResponse,
                };
              }
              return updated;
            });
          }
        }
      }
    } catch (err) {
      console.error("Streaming error", err);
      setMessages((prev) => {
        const updated = [...prev];
        if (updated.length > 0) {
          updated[updated.length - 1] = {
            role: "assistant",
            content: "Disculpa, ha ocurrido un error al conectar con tu estilista. Por favor reinténtalo.",
          };
        }
        return updated;
      });
    } finally {
      setIsStreaming(false);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 font-body">
      {/* Chat Bubble Toggle Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          data-testid="stylist-toggle-button"
          className="flex items-center gap-2 px-6 py-4 bg-text_primary text-white hover:bg-accent_vibrant transition-all duration-150 shadow-xl rounded-none uppercase font-mono-accent tracking-widest text-xs"
          style={{ backgroundColor: "#0A0A0A" }}
        >
          <Sparkles className="w-4 h-4 text-accent_primary" style={{ color: "#4F46E5" }} />
          AI STYLIST // ESTILISTA
        </button>
      )}

      {/* Chat Window Panel */}
      {isOpen && (
        <div
          data-testid="stylist-chat-window"
          className="w-[360px] sm:w-[400px] h-[550px] dark-glass-panel text-white flex flex-col shadow-2xl rounded-none border border-white/10"
          style={{ backgroundColor: "rgba(10, 10, 10, 0.95)" }}
        >
          {/* Header */}
          <div className="p-4 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="font-mono-accent text-xs uppercase tracking-widest text-white">
                WOVLL AI STYLIST
              </span>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              data-testid="stylist-close-button"
              className="text-white/60 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 text-sm scrollbar-thin scrollbar-thumb-white/20">
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center p-6 space-y-3 opacity-80">
                <MessageSquare className="w-8 h-8 text-white/40" />
                <h4 className="font-heading italic text-lg text-white">Consúltale a tu Estilista</h4>
                <p className="text-xs text-white/60 max-w-[250px]">
                  Pregúntame cómo combinar nuestras prendas Wovll, qué tallas elegir o cuáles son las últimas tendencias streetwear.
                </p>
              </div>
            ) : (
              messages.map((msg, idx) => (
                <div
                  key={idx}
                  data-testid={msg.role === "user" ? "stylist-message-user" : "stylist-message-assistant"}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] px-4 py-3 rounded-none ${
                      msg.role === "user"
                        ? "bg-[#4F46E5] text-white"
                        : "bg-white/10 text-white border border-white/5"
                    }`}
                  >
                    <p className="leading-relaxed font-light whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Input Form */}
          <form onSubmit={handleSend} className="p-4 border-t border-white/10 flex gap-2">
            <input
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={isStreaming}
              data-testid="stylist-input"
              placeholder="Pregunta a tu estilista Wovll..."
              className="flex-1 bg-white/5 border border-white/10 rounded-none px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#4F46E5] transition-colors"
            />
            <button
              type="submit"
              disabled={!message.trim() || isStreaming}
              data-testid="stylist-send-button"
              className="px-4 py-2.5 bg-white text-black hover:bg-accent_vibrant hover:text-white disabled:bg-white/20 disabled:text-white/40 transition-all rounded-none"
              style={{ backgroundColor: "#FFFFFF", color: "#000000" }}
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
